import type { FC } from 'react'

import { SalaryRetention } from '@api'
import { Controller, useFormContext } from 'react-hook-form'

import InfoIcon from '@/assets/icons/icon-info.svg'
import { FormController } from '@/components/ui/form/form-controller.tsx'
import { numberConverter } from '@/components/ui/form/utils.ts'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select.tsx'
import { TextField } from '@/components/ui/text-field.tsx'
import { Tooltip } from '@/components/ui/tooltip.tsx'

import type { MemberSalaryType } from './validations.ts'

export interface Props {
  disabled: boolean
}

export const MemberSalaryDialogFormFields: FC<Props> = ({ disabled }) => {
  const form = useFormContext<MemberSalaryType>()

  return (
    <>
      <div>
        <Controller
          name="fixedSalary.amount"
          control={form.control}
          render={({ field }) => (
            <FormController>
              <FormController.Label>Fixed Salary</FormController.Label>
              <FormController.Input aria-disabled={disabled}>
                <TextField
                  value={numberConverter.output(field.value)}
                  onChange={(event) =>
                    field.onChange(numberConverter.input(event.target.value))
                  }
                  prefix="$"
                  disabled={disabled}
                  testId="fixed-salary-input"
                  suffix={
                    <Controller
                      name="fixedSalary.retention"
                      control={form.control}
                      render={({ field }) => (
                        <Select
                          onValueChange={field.onChange}
                          defaultValue={SalaryRetention.MONTHLY}
                          value={field.value}
                          disabled={disabled}
                        >
                          <SelectTrigger className="w-[110px] border-none text-right">
                            <SelectValue placeholder="Select Retention">
                              <span className="capitalize">
                                {field.value.toLowerCase()}
                              </span>
                            </SelectValue>
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value={SalaryRetention.MONTHLY}>
                              Monthly
                            </SelectItem>
                            <SelectItem value={SalaryRetention.YEARLY}>
                              Yearly
                            </SelectItem>
                          </SelectContent>
                        </Select>
                      )}
                    />
                  }
                />
              </FormController.Input>
              <FormController.Error>
                {form.formState.errors.fixedSalary?.amount?.message}
              </FormController.Error>
            </FormController>
          )}
        />
      </div>
      <div className="mt-5">
        <Controller
          name="cutOfRevenuePercentage"
          control={form.control}
          render={({ field }) => (
            <FormController>
              <FormController.Label className="flex gap-1">
                Percentage Cut of Revenue
                <Tooltip content="Set the member’s percentage cut of revenue.">
                  <InfoIcon className="h-4 w-4 opacity-50" />
                </Tooltip>
              </FormController.Label>
              <FormController.Input>
                <TextField
                  value={numberConverter.output(field.value)}
                  onChange={(event) =>
                    field.onChange(numberConverter.input(event.target.value))
                  }
                  prefix="%"
                  disabled={disabled}
                  testId="cut-of-revenue-input"
                />
              </FormController.Input>
              <FormController.Error>
                {form.formState.errors.cutOfRevenuePercentage?.message}
              </FormController.Error>
            </FormController>
          )}
        />
      </div>
    </>
  )
}
